import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { IPlatformHomeMagicBar } from '@shared/components/platform-tool/platform-tool-magic-bar/models/magic-bar-models';
import { HomeSideBarItem } from '@shared/models/platform-tool-models';
import { PlatformQuery } from '@shared/models/platform-query/platform-query';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { Api } from '@shared/constants/common-field.constants';
import { EliteformTableOfContentsService } from '@shared/components/platform-tool/platform-tool-dialog-definition/services/eliteform-table-of-contents.service';
import { FlexSheetService } from '@features/spreadsheet/services/flex-sheet.service';
import { FlexSheetNavigationService } from '@features/spreadsheet/services/flex-sheet-navigation.service';
import { Configuration } from '@shared/models/configuration';
import { CommandService } from '@core/services/command.service';
import { PlatformCommand } from '@shared/commands/command-base';
import { take, takeUntil, timeout } from 'rxjs/operators';
import { XmlViewerHighlightService } from '@shared/components/platform-tool/platform-tool-code-editor-v2/xml-viewer-tabs/xml-viewer-highlight.service';
import { FileManagementService } from '@core/services/file-management.service';
import { XatBotDataService } from '@features/xatbot/services/xat-bot-data.service';
import { SnackBarService } from '@core/services/snack-bar.service';
import { DialogService } from '@shared/services/dialog-service';
import { DialogModel } from '@shared/models/dialog.model';
import { Subject } from 'rxjs';
import { PlatformToolLayoutService } from '@core/services/platform-tool-layout.service';

export type OecdBucket = 'file' | 'severe' | 'other';

/**
 * How long the sidebar waits for a validation command to report back — 15 minutes.
 *
 * The wait is push-based: the command's completion arrives over the notification channel, which has no
 * time limit of its own, so this timeout is the ONLY thing that ends the wait. It is the client's
 * patience, not a mirror of any server-side budget — the worker persists its result to the validation
 * store before it notifies anyone, so giving up here never discards work; it only stops the spinner, and
 * re-opening the sidebar reads whatever landed.
 *
 * Deliberately an RxJS timeout rather than the platform's MaxRetryCount polling knob: the poller stops
 * silently at its cap (notification-service.ts) leaving the spinner running forever, whereas a timeout
 * errors the stream so the failure is actually shown.
 */
const VALIDATION_WAIT_CAP_MS = 15 * 60 * 1_000;

function parseNumericRuleId(ruleId: string | undefined): number | null {
  if (!ruleId) return null;
  const n = parseInt(ruleId, 10);
  return Number.isFinite(n) ? n : null;
}

function bucketFromErrorType(errorType: string | undefined | null): OecdBucket | null {
  const t = (errorType ?? '').toLowerCase();
  if (t.includes('file')) return 'file';
  if (t.includes('severe')) return 'severe';
  if (t.includes('other')) return 'other';
  return null;
}

export interface TransmissionErrorMetaData {
  dataItemId?: string;
  fieldKey?: string;
  fieldTitle?: string;
  dataType?: string;
  pageIndex?: number;
  Schedule?: string;
  Section?: string;
  SourceRef?: string;
  SourceRefIsNavigable?: boolean;
  SourceRefSheetName?: string;
  SourceRefCellAddress?: string;
  SourceRefRow?: number;
  WorkbookId?: string;
  WorksheetId?: string;
  Row?: number;
  Column?: number;
  RowEnd?: number;
  ColumnEnd?: number;
  GridRow?: number;
  GridColumn?: number;
  HasExcelMappingException?: boolean;
  TicketId?: string;
  TicketDisplayId?: string;
  TicketTitle?: string;
  TicketCreationFailed?: boolean;
  TicketCreationReason?: string;
  XmlPath?: string;
  XmlField?: string;
  [key: string]: unknown;
}

export interface TransmissionError {
  id: string;
  errorType: string;
  message: string;
  exceptionDetail: string;
  resolvePath: string;
  fieldPath: string;
  metaData: TransmissionErrorMetaData;
  errorSequenceId: number;
  originalValidationMessage?: string;
  ruleId?: string;
  countryRuleIds?: string[];
  countryCode?: string;
  isUnenforced?: boolean;
  /** Generic tooltip message from the validation workbook: the reason the row
   *  is unenforced when isUnenforced is true, otherwise an informational notice
   *  for the enforced code (if any). Empty when neither applies; the tooltip
   *  then falls back to the validation rule. */
  tooltipMessage?: string;
}

// Drives the colour of the "Notes" pill on each error row and the category
// pill in the Validation Notes dialog header.
export type ValidationNoteCategory = 'reject' | 'warning' | 'info';

export interface ValidationNote {
  errorCode: string;
  /** Canonical lower-case category — drives styling. */
  category: ValidationNoteCategory;
  /** Original PascalCase label from the catalogue (e.g. "Reject", "Warning"). */
  categoryLabel: string;
  issue: string;
  summary: string;
  detail: string;
  /** Pre-written question the user can ask XatBot — shown as the dialog's
   *  "Suggested question". (The backend-only SystemPrompt is never sent here.) */
  question: string;
}

export interface PublishedValidationRule {
  ruleId: string;
  validationRule: string;
  validationMessage: string;
  countryCode: string;
  countryRuleIds?: string[];
  errorType?: string;
  /** On a bundle .sch's stub (FM-P-314): the authority whose rule it is, e.g. "ATO". The chip reads
   *  "<code> (<authority>)" and the bucket is the stub's own Severe/Other. Absent on GIR's stubs. */
  authority?: string;
  note?: ValidationNote;
}

export interface BusinessErrorGroupRow {
  ruleId: string;
  bucket: OecdBucket;
  summaryMessage: string;
  instances: TransmissionError[];
  groupIndex: number;
}

export interface AllRulesPath {
  path: string;
  sourceRef: string;
  sheetName?: string | null;
  cellAddress?: string | null;
  row?: number | null;
  isNavigable: boolean;
}

export interface AllRulesEntry {
  ruleId: string;
  bucket: OecdBucket;
  errorType?: string;
  validationRule?: string;
  validationMessage?: string;
  countryCode?: string;
  targetPath?: string;
  referencePath?: string;
  pathCount: number;
  paths: AllRulesPath[];
}

@Component({
  selector: 'app-elite-form-validation-error',
  standalone: false,
  templateUrl: './elite-form-validation-error.component.html',
  styleUrl: './elite-form-validation-error.component.scss'
})
export class EliteFormValidationErrorComponent implements IPlatformHomeMagicBar, OnInit, OnDestroy {

  homeSideBarItem: HomeSideBarItem;
  schemaId: any;
  responseId: any;
  formAutoId: any;
  formName: any;
  submissionAutoId: any;
  isClientSchema: any;
  currentFormType: 'elite' | 'excel' | 'xml' | null = null;
  hideGotoField = false;

  private tabState: Record<'validation' | 'business' | 'allRules', {
    errors: TransmissionError[];
    isLoading: boolean;
    isInitialLoading: boolean;
    hasGenerated: boolean;
    isProcessComplete: boolean;
    publishedValidationRules: PublishedValidationRule[];
    rulesById: Map<string, PublishedValidationRule>;
    selectedRuleIds: string[];
    selectedOecdBuckets: OecdBucket[];
    isStale: boolean;
    lastGeneratedAt: Date | null;
    tolerancePercentEnabled: boolean;
    hideUnenforced: boolean;
  }> = {
    validation: {
      errors: [],
      isLoading: false,
      isInitialLoading: false,
      hasGenerated: false,
      isProcessComplete: false,
      publishedValidationRules: [],
      rulesById: new Map(),
      selectedRuleIds: [],
      selectedOecdBuckets: [],
      isStale: false,
      lastGeneratedAt: null,
      tolerancePercentEnabled: true,
      hideUnenforced: true
    },
    business: {
      errors: [],
      isLoading: false,
      isInitialLoading: false,
      hasGenerated: false,
      isProcessComplete: false,
      publishedValidationRules: [],
      rulesById: new Map(),
      selectedRuleIds: [],
      selectedOecdBuckets: [],
      isStale: false,
      lastGeneratedAt: null,
      tolerancePercentEnabled: true,
      hideUnenforced: true
    },
    allRules: {
      errors: [],
      isLoading: false,
      isInitialLoading: false,
      hasGenerated: false,
      isProcessComplete: false,
      publishedValidationRules: [],
      rulesById: new Map(),
      selectedRuleIds: [],
      selectedOecdBuckets: [],
      isStale: false,
      lastGeneratedAt: null,
      tolerancePercentEnabled: true,
      hideUnenforced: true
    }
  };

  private expandedBusinessGroupRuleIds = new Set<string>();
  expandedBucketAccordion: OecdBucket | null = null;

  // The queries are read-only — they return the last SAVED validation result. Regenerating is a
  // command (the write half); a forced refresh fires it first and then re-reads the query.
  private tabConfig: Record<'validation' | 'business' | 'allRules', { queryName: string; responseKey: string; commandName?: string; }> = {
    validation: { queryName: 'GetValidationErrorsQuery',            responseKey: 'ValidationErrors', commandName: 'ValidateXmlSchemaErrorCommand'   },
    business:   { queryName: 'GetBusinessErrorsQuery',              responseKey: 'BusinessErrors',   commandName: 'ValidateXmlBusinessErrorCommand' },
    allRules:   { queryName: 'GetXmlPlaygroundAllRulesQuery',       responseKey: 'AllRules'         }
  };

  activeTab: 'validation' | 'business' | 'allRules' = 'validation';

  allRulesEntries: AllRulesEntry[] = [];
  allRulesExpanded = new Set<string>();
  allRulesSearchTerm = '';
  allRulesSelectedBuckets: OecdBucket[] = [];
  isAllRulesAvailable = false;
  /** The Additional Validation tab runs the bundle's .sch (FM-P-314): no File Errors card, no unenforced toggle. */
  isBundleBusinessRules = false;
  isXmlPlaygroundEntry = false;
  private hasLoadedAllRulesOnce = false;

  private get current() { return this.tabState[this.activeTab]; }

  get validationErrors(): TransmissionError[] { return this.current.errors; }
  set validationErrors(v: TransmissionError[]) {
    this.current.errors = v;
    const first = this.sortBySequenceId(v)[0];
    this.selectedErrorSequenceId = first?.errorSequenceId ?? null;
    this.refreshDerivedState();
  }

  get isLoading(): boolean { return this.current.isLoading; }
  set isLoading(v: boolean) { this.current.isLoading = v; }

  get isInitialLoading(): boolean { return this.current.isInitialLoading; }
  set isInitialLoading(v: boolean) { this.current.isInitialLoading = v; }

  get isStale(): boolean { return this.current.isStale; }
  set isStale(v: boolean) { this.current.isStale = v; }

  get lastGeneratedAt(): Date | null { return this.current.lastGeneratedAt; }
  set lastGeneratedAt(v: Date | null) { this.current.lastGeneratedAt = v; }

  get tolerancePercentEnabled(): boolean { return true; }
  set tolerancePercentEnabled(_v: boolean) { /* locked ON; toggle hidden */ }

  get hideUnenforced(): boolean { return this.current.hideUnenforced; }
  set hideUnenforced(v: boolean) {
    this.current.hideUnenforced = v;
    this.pruneSelectedRuleIdsAfterDataChange();
    this.refreshDerivedState();
  }

  get hasGenerated(): boolean { return this.current.hasGenerated; }
  set hasGenerated(v: boolean) { this.current.hasGenerated = v; }

  get isProcessComplete(): boolean { return this.current.isProcessComplete; }
  set isProcessComplete(v: boolean) { this.current.isProcessComplete = v; }

  get publishedValidationRules(): PublishedValidationRule[] { return this.current.publishedValidationRules; }
  set publishedValidationRules(v: PublishedValidationRule[]) {
    this.current.publishedValidationRules = v;
    this.current.rulesById = new Map(v.map(r => [r.ruleId, r]));
    this.refreshDerivedState();
  }

  get rulesById(): Map<string, PublishedValidationRule> { return this.current.rulesById; }

  get selectedRuleIds(): string[] { return this.current.selectedRuleIds; }
  set selectedRuleIds(v: string[]) { this.current.selectedRuleIds = [...v]; }

  get selectedOecdBuckets(): OecdBucket[] { return this.current.selectedOecdBuckets; }
  set selectedOecdBuckets(v: OecdBucket[]) {
    this.current.selectedOecdBuckets = [...v];
    this.refreshDerivedState();
  }

  isDownloading = false;
  selectedErrorSequenceId: number | null = null;
  unSub$ = new Subject<void>();
  @Output() rowClick = new EventEmitter<TransmissionError>();

  readonly toleranceToggleTooltip =
    'The OECD validation rules permits a 1% tolerance on certain values, meaning small variances may still be accepted by the receiving authority without triggering an error. With this toggle ON, the generated list of errors respects that tolerance threshold and only flags mismatches exceeding 1%.\n\n' +
    'Disabling this toggle removes the tolerance and is likely to display a substantially higher count of validation errors. Use with caution, the additional errors may be informational rather than blocking.';

  readonly hideUnenforcedToggleTooltip =
    'Enable this toggle to hide errors that the local taxing authority has indicated will not be enforced for the 2024 filing year.';

  readonly oecdCategories: ReadonlyArray<{
    bucket: OecdBucket;
    shortLabel: string;
    metricLabel: string;
    tooltipHeading: string;
    codes: string;
    tooltip: string;
  }> = [
    {
      bucket: 'file',
      shortLabel: 'File Errors',
      metricLabel: 'File Errors',
      tooltipHeading: 'File Error',
      codes: 'codes 50001–50011',
      tooltip:
        'A fundamental issue (e.g., file can\'t be decrypted, fails schema validation, contains a virus, or was sent to the wrong jurisdiction) that prevents the tax authority from opening your file. Fix before submitting — the authority will reject it and require resubmission with a new MessageRefID.'
    },
    {
      bucket: 'severe',
      shortLabel: 'Severe Errors',
      metricLabel: 'Severe Errors',
      tooltipHeading: 'Severe Record Error',
      codes: 'codes 60001–60028',
      tooltip:
        'A core data issue that makes your file unusable — for example, malformed IDs, duplicate DocRefIDs, mixed new/correction records, or ETR and Top-Up Tax calculations that don\'t reconcile. Fix before submitting — the authority is expected to reject the file as a whole.'
    },
    {
      bucket: 'other',
      shortLabel: 'Other Errors',
      metricLabel: 'Other Errors',
      tooltipHeading: 'Other Record Error',
      codes: 'codes 70001–70124',
      tooltip:
        'A data quality issue (e.g., TIN formatting, ownership consistency, safe harbour eligibility, internal calculation cross-checks) that doesn\'t block the file from being opened. The OECD does not require that jurisdictions treat 70000-series errors as mandatory, but it is recommended to fix these errors before submitting.'
    }
  ];

  /** The cards on the Additional Validation tab: all three, or Severe and Other for a bundle's rules (set in ngOnInit). */
  metricCategories = this.oecdCategories;

  constructor(
    private platformQueryService: PlatformQueryService,
    private tocService: EliteformTableOfContentsService,
    private flexSheetService: FlexSheetService,
    private flexSheetNavigationService: FlexSheetNavigationService,
    private commandService: CommandService,
    private xmlHighlightService: XmlViewerHighlightService,
    private fileManagementService: FileManagementService,
    private xatBotDataService: XatBotDataService,
    private snackBarService: SnackBarService,
    private dialogService: DialogService,
    public platformToolLayoutService: PlatformToolLayoutService,
  ) { }

  ngOnInit(): void {
    this.responseId = this.homeSideBarItem.fieldValues['ResponseId'];
    this.formAutoId = this.homeSideBarItem.fieldValues['FormAutoId'];
    this.formName = this.homeSideBarItem.fieldValues['FormName'];
    this.schemaId = this.homeSideBarItem.fieldValues['SchemaId'];
    this.submissionAutoId = this.homeSideBarItem.fieldValues['SubmissionAutoId'];
    this.isClientSchema = this.homeSideBarItem.fieldValues['IsClientSchema'];
    this.isAllRulesAvailable = !!this.homeSideBarItem.fieldValues['IsAllRulesAvailable'];
    this.isBundleBusinessRules = !!this.homeSideBarItem.fieldValues['IsBundleBusinessRules'];
    if (this.isBundleBusinessRules) {
      this.metricCategories = this.oecdCategories.filter(cat => cat.bucket !== 'file');
    }
    this.isXmlPlaygroundEntry = !!this.homeSideBarItem.fieldValues['IsXmlPlaygroundEntry'];
    this.currentFormType = this.homeSideBarItem.fieldValues['CurrentFormType'] ?? null;
    this.hideGotoField = !!this.homeSideBarItem.fieldValues['HideGotoField'];
    if (this.homeSideBarItem.fieldValues['IsValidationRuleAvailable'] === false
        && this.homeSideBarItem.fieldValues['IsBusinessRuleAvailable'] === true) {
      this.activeTab = 'business';
    }

    this.loadValidationErrors(true);
  }

  loadValidationErrors(force = false): void {
    if (this.responseId == null) {
      this.validationErrors = [];
      return;
    }

    if (force) {
      this.isLoading = true;
      this.regenerateThenFetch();
    } else {
      this.isInitialLoading = true;
      this.fetchSavedValidationErrors();
    }
  }

  /**
   * Fires the tab's validation command (the write half) and, once it has persisted the fresh
   * errors, re-reads them through the read-only query. Tabs with no command (All Rules) and
   * XML-playground entries — which the query always computes fresh and never persists — skip
   * straight to the read.
   */
  private regenerateThenFetch(): void {
    const config = this.tabConfig[this.activeTab];
    if (!config.commandName || this.isXmlPlaygroundEntry) {
      this.fetchSavedValidationErrors();
      return;
    }

    const command = new PlatformCommand(config.commandName, Api.FilingManagerApi);
    command.setValue('ResponseId', this.responseId);
    command.setValue('SchemaId', this.schemaId);
    command.setValue('SubmissionAutoId', this.submissionAutoId);
    command.setValue('IsClientSchema', this.isClientSchema);
    command.setValue('TolerancePercentEnabled', this.tolerancePercentEnabled);
    this.applyExtraFieldValues((key, value) => command.setValue(key, value));

    this.commandService.executePlatformCommand(command).pipe(
      takeUntil(this.unSub$),
      timeout(VALIDATION_WAIT_CAP_MS)
    ).subscribe({
      next: (commandResponse) => {
        // A failed regeneration (mapping/template error, or no resolvable template config) shows the same
        // fallback the query used to render when it could not complete the validation itself.
        const mappingError = commandResponse?.fieldValues?.['MappingError'];
        if (mappingError || commandResponse?.fieldValues?.['IsSuccess'] === false) {
          this.renderMappingErrorFallback(mappingError);
          return;
        }
        this.fetchSavedValidationErrors();
      },
      // Covers a genuine command failure AND the 15-minute timeout above. Either way the spinner must
      // stop; the validation may still be running on the worker, and because its result is persisted
      // independently of this session, re-opening the sidebar picks it up.
      error: () => this.renderMappingErrorFallback()
    });
  }

  /** Copies the step-scoped extras the sidebar carries (IsAmendment, evaluationMode, …) onto a query or command. */
  private applyExtraFieldValues(set: (key: string, value: unknown) => void): void {
    const extras = this.homeSideBarItem.fieldValues['QueryFieldValues'];
    if (!extras) return;
    for (const key in extras) {
      if (extras.hasOwnProperty(key)) {
        set(key, extras[key]);
      }
    }
  }

  private fetchSavedValidationErrors(): void {
    const config = this.tabConfig[this.activeTab];
    const query = new PlatformQuery();
    query.name = config.queryName;
    query.api = Api.FilingManagerApi;
    query.fieldValues['ResponseId']              = this.responseId;
    query.fieldValues['SchemaId']                = this.schemaId;
    query.fieldValues['SubmissionAutoId']        = this.submissionAutoId;
    query.fieldValues['IsClientSchema']          = this.isClientSchema;
    query.fieldValues['TolerancePercentEnabled'] = this.tolerancePercentEnabled;
    this.applyExtraFieldValues((key, value) => query.fieldValues[key] = value);
    this.platformQueryService.executeQuery(query).pipe(
      takeUntil(this.unSub$)
    ).subscribe({
      next: (response) => {
        this.isProcessComplete = response.fieldValues['IsProcessComplete'] ?? false;
        const hasSaved = response.fieldValues['HasSavedResult'] === true;

        if (this.isProcessComplete && hasSaved) {
          const rawErrors = response.fieldValues[config.responseKey];
          this.validationErrors = Array.isArray(rawErrors)
            ? rawErrors.map((e: any) => this.normalizeError(e))
            : [];

          const rawRules = response.fieldValues['PublishedValidationRules'];
          this.publishedValidationRules = Array.isArray(rawRules)
            ? rawRules.map((r: any) => this.normalizePublishedRule(r))
            : [];
          this.pruneSelectedRuleIdsAfterDataChange();

          this.isStale = response.fieldValues['IsStale'] === true;
          const lastGen = response.fieldValues['LastGeneratedAt'];
          this.lastGeneratedAt = lastGen ? new Date(lastGen) : null;
          if (response.fieldValues['IsTolerancePercentEnabled'] !== undefined) {
            this.tolerancePercentEnabled = response.fieldValues['IsTolerancePercentEnabled'] === true;
          }
          this.hasGenerated = true;
        } else if (this.isProcessComplete) {
          this.validationErrors = [];
          this.publishedValidationRules = [];
          this.selectedRuleIds = [];
          this.selectedOecdBuckets = [];
          this.expandedBusinessGroupRuleIds.clear();
          this.isStale = false;
          this.lastGeneratedAt = null;
          this.hasGenerated = false;
        } else {
          this.renderMappingErrorFallback();
          return;
        }

        this.isLoading = false;
        this.isInitialLoading = false;
      },
      error: () => this.renderMappingErrorFallback()
    });
  }

  private renderMappingErrorFallback(rawMappingError?: any): void {
    this.validationErrors = [
      this.normalizeError(rawMappingError ?? { MetaData: { HasExcelMappingException: true } })
    ];
    this.publishedValidationRules = [];
    this.selectedRuleIds = [];
    this.selectedOecdBuckets = [];
    this.expandedBusinessGroupRuleIds.clear();
    this.isStale = false;
    this.lastGeneratedAt = null;
    this.isLoading = false;
    this.isInitialLoading = false;
    this.hasGenerated = true;
    // Required for the error panel to render at all: hasMappingIssue gates on !isProcessComplete, and on
    // the error paths (a command failure, or the wait timing out) nothing else clears the flag — so a
    // failure that follows an earlier successful load would leave it true and show a blank sidebar.
    this.isProcessComplete = false;
  }

  refresh(): void {
    this.loadValidationErrors(true);
  }

  openCreateSupportTicketDialog(): void {
    if (!this.responseId) return;
    const command = new PlatformCommand('AddTaskDialogCommand', Api.TaskManager);
    command.setValue('ticketId', this.responseId);
    command.title = 'Create Support Ticket';
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe({
      next: () => this.loadValidationErrors(),
    });
  }

  downloadXmlAndValidationErrors(): void {
    if (this.isDownloading) return;
    this.isDownloading = true;
    const commandName = this.activeTab === 'business'
      ? 'DownloadXmlAndBusinessErrorsAsZipCommand'
      : 'DownloadXmlAndValidationErrorsAsZipCommand';
    const command = new PlatformCommand(commandName, Api.FilingManagerApi);
    command.setValue('ResponseId', this.responseId);
    command.setValue('SchemaId', this.schemaId);
    command.setValue('SubmissionAutoId', this.submissionAutoId);
    command.setValue('IsClientSchema', this.isClientSchema);
    if (this.activeTab === 'business') {
      command.setValue('TolerancePercentEnabled', this.tolerancePercentEnabled);
      command.setValue('HideUnenforced', this.hideUnenforced);
    }
    // Forward the step-scoped QueryFieldValues (e.g. evaluationMode) the same way the
    // validation query does, so the downloaded report runs the exact rule set the
    // on-screen list ran.
    if (this.homeSideBarItem.fieldValues['QueryFieldValues']) {
      for (const key in this.homeSideBarItem.fieldValues['QueryFieldValues']) {
        if (this.homeSideBarItem.fieldValues['QueryFieldValues'].hasOwnProperty(key)) {
          command.setValue(key, this.homeSideBarItem.fieldValues['QueryFieldValues'][key]);
        }
      }
    }
    this.commandService.executePlatformCommand(command).pipe(takeUntil(this.unSub$)).subscribe({
      next: commandResponse => {
        const downloadResponseModel = commandResponse?.fieldValues?.['DownloadFileResponseModel'];
        if (!downloadResponseModel?.fileId) {
          this.isDownloading = false;
          return;
        }
        this.fileManagementService.downloadClientFileWithId(downloadResponseModel.fileId).subscribe({
          next: payload => {
            this.fileManagementService.openSaveFileWindow(payload, downloadResponseModel.fileName);
            this.isDownloading = false;
          },
          error: () => { this.isDownloading = false; }
        });
      },
      error: () => { this.isDownloading = false; }
    });
    this.dialogService.dialogStatus.pipe(
      take(1)).subscribe(status => {
      if(status === 'Close') {
        this.isDownloading = false;
      }
    });
  }

  private normalizeError(e: any): TransmissionError {
    const rawMeta = e.MetaData ?? e.metaData ?? {};
    const metaData: TransmissionErrorMetaData = {
      dataItemId: rawMeta.DataItemId ?? rawMeta.dataItemId ?? '',
      fieldKey: rawMeta.FieldKey ?? rawMeta.fieldKey ?? '',
      fieldTitle: rawMeta.FieldTitle ?? rawMeta.fieldTitle ?? '',
      dataType: rawMeta.DataType ?? rawMeta.dataType ?? '',
      pageIndex: Number(rawMeta.PageIndex ?? rawMeta.pageIndex ?? -1),

      WorkbookId: rawMeta.WorkbookId ?? rawMeta.workbookId ?? '',
      WorksheetId: rawMeta.WorksheetId ?? rawMeta.worksheetId ?? '',
      Row: Number(rawMeta.Row ?? rawMeta.row ?? -1),
      Column: Number(rawMeta.Column ?? rawMeta.column ?? -1),
      RowEnd: Number(rawMeta.RowEnd ?? rawMeta.rowEnd ?? -1),
      ColumnEnd: Number(rawMeta.ColumnEnd ?? rawMeta.columnEnd ?? -1),
      GridRow: Number(rawMeta.GridRow ?? rawMeta.gridRow ?? -1),
      GridColumn: Number(rawMeta.GridColumn ?? rawMeta.gridColumn ?? -1),
      HasExcelMappingException: rawMeta.HasExcelMappingException ?? rawMeta.hasExcelMappingException ?? false,
      TicketId: rawMeta.TicketId ?? rawMeta.ticketId ?? '',
      TicketDisplayId: rawMeta.TicketDisplayId ?? rawMeta.ticketDisplayId ?? '',
      TicketTitle: rawMeta.TicketTitle ?? rawMeta.ticketTitle ?? '',
      SourceRef: rawMeta.SourceRef ?? rawMeta.sourceRef ?? '',
      SourceRefIsNavigable: (rawMeta.SourceRefIsNavigable ?? rawMeta.sourceRefIsNavigable) === true,
      SourceRefSheetName: rawMeta.SourceRefSheetName ?? rawMeta.sourceRefSheetName ?? '',
      SourceRefCellAddress: rawMeta.SourceRefCellAddress ?? rawMeta.sourceRefCellAddress ?? '',
      SourceRefRow: Number(rawMeta.SourceRefRow ?? rawMeta.sourceRefRow ?? -1),
      ...rawMeta
    };
    return {
      id: e.Id,
      errorType: e.ErrorType ?? e.errorType ?? '',
      message: e.Message ?? e.message ?? '',
      exceptionDetail: e.ExceptionDetail ?? e.exceptionDetail ?? '',
      resolvePath: e.ResolvePath ?? e.resolvePath ?? '',
      fieldPath: e.FieldPath ?? e.fieldPath ?? '',
      errorSequenceId: Number(e.ErrorSequenceId ?? e.errorSequenceId ?? 0) || 0,
      originalValidationMessage: e.OriginalValidationMessage ?? e.originalValidationMessage,
      ruleId: this.stripRulePrefix(e.RuleId ?? e.ruleId ?? ''),
      countryRuleIds: this.normalizeCountryRuleIds(e.CountryRuleIds ?? e.countryRuleIds),
      countryCode: (e.CountryCode ?? e.countryCode ?? '').toUpperCase(),
      isUnenforced: (e.IsUnenforced ?? e.isUnenforced) === true,
      tooltipMessage: e.TooltipMessage ?? e.tooltipMessage ?? '',
      metaData
    };
  }

  private normalizeCountryRuleIds(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
      .map(id => this.stripRulePrefix(id));
  }

  private normalizePublishedRule(r: any): PublishedValidationRule {
    const et = r.ErrorType ?? r.errorType;
    const errorTypeTrim = et == null || et === '' ? '' : String(et).trim();
    const ruleId = this.stripRulePrefix(r.RuleId ?? r.ruleId ?? '');
    const note = this.normalizeValidationNote(r.ValidationNote ?? r.validationNote, ruleId);
    const authority = String(r.Authority ?? r.authority ?? '').trim();
    return {
      ruleId,
      validationRule: r.ValidationRule ?? r.validationRule ?? '',
      validationMessage: r.ValidationMessage ?? r.validationMessage ?? '',
      countryCode: r.CountryCode ?? r.countryCode ?? '',
      countryRuleIds: this.normalizeCountryRuleIds(r.CountryRuleIds ?? r.countryRuleIds),
      ...(errorTypeTrim ? { errorType: errorTypeTrim } : {}),
      ...(authority ? { authority } : {}),
      ...(note ? { note } : {})
    };
  }

  private normalizeValidationNote(raw: any, fallbackErrorCode: string): ValidationNote | undefined {
    if (!raw || typeof raw !== 'object') return undefined;
    const pick = (pascal: string, camel: string): string => {
      const v = raw[pascal] ?? raw[camel];
      return v == null ? '' : String(v).trim();
    };
    const issue = pick('Issue', 'issue');
    const summary = pick('Summary', 'summary');
    const detail = pick('Detail', 'detail');
    // A note without any narrative content adds no value to the FE — drop it
    // so the Notes pill / dialog opener don't fire on hollow records.
    if (!issue && !summary && !detail) return undefined;

    const rawCategory = pick('Category', 'category');
    const category = this.normalizeCategory(rawCategory);
    const categoryLabel = rawCategory ? this.toTitleCase(rawCategory) : this.toTitleCase(category);

    return {
      errorCode: pick('ErrorCode', 'errorCode') || fallbackErrorCode,
      category,
      categoryLabel,
      issue,
      summary,
      detail,
      question: pick('Question', 'question')
    };
  }

  private normalizeCategory(raw: string): ValidationNoteCategory {
    const v = (raw ?? '').toLowerCase();
    if (v.startsWith('reject')) return 'reject';
    if (v.startsWith('warn'))   return 'warning';
    if (v.startsWith('info'))   return 'info';
    // Empty / unknown defaults to warning to match the screenshot's yellow
    // dot on the unlabelled row 70121.
    return 'warning';
  }

  private toTitleCase(s: string): string {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }

  private stripRulePrefix(id: string): string {
    return (id ?? '').replace(/^Rule-/i, '');
  }

  resolveOecdBucket(ruleId: string | undefined): OecdBucket | null {
    const id = ruleId?.trim();
    if (!id) return null;
    const rule = this.rulesById.get(id);
    // A bundle's rule (FM-P-314) counts under its own Severe/Other, as its stub says.
    if (rule?.authority) return bucketFromErrorType(rule.errorType);
    // Country-specific rules (rule's countryCode is set and isn't OECD) are
    // surfaced under the "Other" bucket rather than their own category.
    const cc = (rule?.countryCode ?? '').toUpperCase();
    if (cc && cc !== 'OECD') return 'other';
    return bucketFromErrorType(rule?.errorType);
  }

  getValidationNote(ruleId: string | null | undefined): ValidationNote | null {
    if (!ruleId || ruleId === '__UNKNOWN__') return null;
    return this.rulesById.get(ruleId)?.note ?? null;
  }

  openValidationNotesDialog(ruleId: string, event: Event): void {
    event?.stopPropagation();
    const note = this.getValidationNote(ruleId);
    if (!note) return;

    const ruleLabel = this.displayBadgeLabel(ruleId);
    const dialogModel = DialogModel.create(
      'Validation Details',
      'EliteFormValidationNotesDialogContentComponent',
      { note, ruleLabel, ruleId },
      '560px',  // minWidth
      '720px',  // maxWidth
      false,    // isDisabledOkButton
      -1,       // maxLength
      '',       // checkboxCaption
      'Close',  // okCaption
      '',       // optionalButtonsCaption
      true      // isDisabledOptionalButtonsCaption
    );
    dialogModel.cancelCaption = '';

    this.dialogService.showOkCancelDialog(dialogModel).pipe(take(1)).subscribe();
  }

  // True when the error's rule has an editorial ValidationNote (i.e. "priority
  // details"). Drives the inline priority info-badge and the "Details" button.
  hasValidationNote(error: TransmissionError): boolean {
    return this.getValidationNote(this.effectiveRuleIdForError(error)) != null;
  }

  // Opens the Validation Details dialog for an error row's rule. Resolves the
  // rule id internally so the template doesn't pass a possibly-null id.
  openValidationDetailsDialog(error: TransmissionError, event: Event): void {
    const rid = this.effectiveRuleIdForError(error);
    if (rid) this.openValidationNotesDialog(rid, event);
  }

  private primaryErrorNarrative(error: TransmissionError): string {
    if (error.message?.trim()) return error.message;
    if (error.exceptionDetail?.trim()) return error.exceptionDetail;
    return '';
  }

  effectiveRuleIdForError(error: TransmissionError): string | null {
    return error.ruleId?.trim() || null;
  }

  ruleGroupingKey(error: TransmissionError): string {
    return this.effectiveRuleIdForError(error) ?? '__UNKNOWN__';
  }

  displayErrorBodyText(error: TransmissionError): string {
    return this.primaryErrorNarrative(error).trimEnd();
  }

  private pruneSelectedRuleIdsAfterDataChange(): void {
    const allowed = new Set(this.businessCodesForDropdownRaw.map(r => r.ruleId));
    if (this.hasAnyNotedErrors) {
      allowed.add(EliteFormValidationErrorComponent.NOTES_FILTER_ID);
    }
    const next = this.selectedRuleIds.filter(id => allowed.has(id));
    if (next.length !== this.selectedRuleIds.length) {
      this.selectedRuleIds = next;
    }
  }

  get filteredValidationErrors(): TransmissionError[] {
    return this.sortBySequenceId(this.validationErrors);
  }

  // Errors visible after the global "Hide unenforced errors" toggle.
  // Applied before bucket/code filters so OECD counts and the
  // "Filter by error code" dropdown reflect what the user actually sees.
  private get displayedErrors(): TransmissionError[] {
    return this.hideUnenforced
      ? this.validationErrors.filter(e => !e.isUnenforced)
      : this.validationErrors;
  }

  private get businessErrorsAfterTypeFilter(): TransmissionError[] {
    const buckets = this.selectedOecdBuckets;
    const base = this.displayedErrors;
    if (buckets.length === 0) return [...base];
    const set = new Set(buckets);
    return base.filter(e => {
      const b = this.resolveOecdBucket(this.effectiveRuleIdForError(e) ?? undefined);
      return b != null && set.has(b);
    });
  }

  private get businessCodesForDropdownRaw(): PublishedValidationRule[] {
    return this.ruleRowsForDistinctIds(this.businessErrorsAfterTypeFilter);
  }

  private ruleRowsForDistinctIds(errors: TransmissionError[]): PublishedValidationRule[] {
    if (errors.length === 0) return [];
    const usedIds = new Set<string>();
    for (const e of errors) {
      const key = this.ruleGroupingKey(e);
      if (key !== '__UNKNOWN__') usedIds.add(key);
    }
    if (usedIds.size === 0) return [];
    const rules: PublishedValidationRule[] = [];
    for (const id of usedIds) {
      const published = this.rulesById.get(id);
      if (published) {
        rules.push(published);
      } else {
        const cc = errors.find(e => this.ruleGroupingKey(e) === id)?.countryCode ?? '';
        rules.push({ ruleId: id, validationRule: '', validationMessage: '', countryCode: cc });
      }
    }
    return rules.sort((a, b) => this.compareRuleIdsNumeric(a.ruleId, b.ruleId));
  }

  private compareRuleIdsNumeric(a: string, b: string): number {
    if (a === '__UNKNOWN__' && b === '__UNKNOWN__') return 0;
    if (a === '__UNKNOWN__') return 1;
    if (b === '__UNKNOWN__') return -1;
    const na = parseNumericRuleId(a);
    const nb = parseNumericRuleId(b);
    if (na != null && nb != null && na !== nb) return na - nb;
    return a.localeCompare(b);
  }

  private sortBySequenceId(errors: TransmissionError[]): TransmissionError[] {
    return [...errors].sort((a, b) => (Number(a.errorSequenceId) || 0) - (Number(b.errorSequenceId) || 0));
  }

  // The dropdown's `codesForDropdown` and `getPairsForRule(ruleId)` used to be
  // getters that allocated fresh arrays/Maps every call. Angular re-evaluates
  // template bindings on every change-detection cycle (often thousands per
  // second), so even cheap allocations turn into a tight loop that pegs the CPU
  // and freezes the page — especially since the OECD catalogue is now ~120
  // rules instead of ~2 synthesised ones. We pre-compute both into plain
  // fields and only update them when source data actually changes (via
  // refreshDerivedState() in the relevant setters). The template now reads
  // stable references and Angular's *ngFor does no diff work per cycle.

  static readonly NOTES_FILTER_ID = '__NOTES__';

  private static readonly EMPTY_PAIRS: ReadonlyArray<{ ruleId: string; countryCode: string }> = [];

  /** Stable, pre-computed list backing `codesForDropdown` getter. */
  private _codesForDropdown: PublishedValidationRule[] = [];
  get codesForDropdown(): PublishedValidationRule[] { return this._codesForDropdown; }

  // ---- In-dropdown "Filter by error code" search ----
  // The <mat-select> is a dropdown, not a text input, so users can't type to
  // narrow a long (~120-row) OECD catalogue. We render a sticky search box at
  // the top of the panel that filters the option list by code + message.
  // Following the perf note above, the filtered list is a pre-computed field
  // refreshed only when source data or the search term changes — never a
  // getter that re-filters on every change-detection cycle.

  /** Search term typed into the in-dropdown "Filter by error code" box. */
  businessCodeFilterSearch = '';

  /** Stable, pre-computed `codesForDropdown` narrowed by `businessCodeFilterSearch`. */
  private _filteredCodesForDropdown: PublishedValidationRule[] = [];
  get filteredCodesForDropdown(): PublishedValidationRule[] { return this._filteredCodesForDropdown; }

  /** Whether the "Notes (n)" sentinel row matches the active search term. */
  private _notesRowMatchesSearch = true;
  get notesRowMatchesSearch(): boolean { return this._notesRowMatchesSearch; }

  /** Stable, pre-computed map: OECD ruleId -> array of { ruleId, countryCode } pairs. */
  private _pairsByRule: Map<string, ReadonlyArray<{ ruleId: string; countryCode: string }>> = new Map();
  getPairsForRule(ruleId: string): ReadonlyArray<{ ruleId: string; countryCode: string }> {
    return this._pairsByRule.get(ruleId) ?? EliteFormValidationErrorComponent.EMPTY_PAIRS;
  }

  /**
   * Recompute the cached fields after validationErrors / publishedValidationRules /
   * selectedOecdBuckets change. Idempotent and cheap; safe to call repeatedly.
   */
  private refreshDerivedState(): void {
    // pairs: OECD ruleId -> [country ruleId, ...]
    const pairs = new Map<string, string[]>();
    for (const err of this.validationErrors) {
      if (!err.ruleId || !err.countryRuleIds?.length) continue;
      const existing = pairs.get(err.ruleId) ?? [];
      for (const cid of err.countryRuleIds) {
        if (!existing.includes(cid)) existing.push(cid);
      }
      pairs.set(err.ruleId, existing);
    }

    // Country ruleIds appearing in any pair — exclude from standalone dropdown rows.
    const paired = new Set<string>();
    for (const ids of pairs.values()) {
      for (const id of ids) paired.add(id);
    }

    // Country ruleId -> country code, for flag rendering.
    const countryCodeByRuleId = new Map<string, string>();
    for (const r of this.publishedValidationRules) {
      if (r.countryCode && r.countryCode !== 'OECD') {
        countryCodeByRuleId.set(r.ruleId, r.countryCode);
      }
    }

    // Pre-build the per-rule pair arrays.
    const nextPairsByRule = new Map<string, ReadonlyArray<{ ruleId: string; countryCode: string }>>();
    for (const [oecdRuleId, ids] of pairs) {
      nextPairsByRule.set(
        oecdRuleId,
        ids.map(id => ({ ruleId: id, countryCode: countryCodeByRuleId.get(id) ?? '' }))
      );
    }
    this._pairsByRule = nextPairsByRule;

    // Pre-build the dropdown list (raw rules minus those that appear as a country pair on some OECD row).
    const raw = this.businessCodesForDropdownRaw;
    this._codesForDropdown = paired.size === 0
      ? raw
      : raw.filter(r => !paired.has(r.ruleId));

    // Keep the search-narrowed list in sync with the source list.
    this.recomputeFilteredCodes();
  }

  /**
   * Narrow `_codesForDropdown` by the current search term (code + message,
   * plus the visible country-pair ids). Cheap and idempotent; called when the
   * source list changes (refreshDerivedState) or the user types.
   */
  private recomputeFilteredCodes(): void {
    const term = (this.businessCodeFilterSearch ?? '').trim().toLowerCase();
    if (!term) {
      this._filteredCodesForDropdown = this._codesForDropdown;
      this._notesRowMatchesSearch = true;
      return;
    }
    // The notes sentinel row is now labelled "Priority details" — keep it
    // findable by either wording.
    this._notesRowMatchesSearch = 'priority details'.includes(term) || 'notes'.includes(term);
    this._filteredCodesForDropdown = this._codesForDropdown.filter(rule => {
      if ((rule.ruleId ?? '').toLowerCase().includes(term)) return true;
      if (this.displayRuleCode(rule.ruleId).toLowerCase().includes(term)) return true;
      if ((rule.validationMessage ?? '').toLowerCase().includes(term)) return true;
      // Also match the country-pair rule ids rendered on this row.
      return this.getPairsForRule(rule.ruleId).some(p =>
        (p.ruleId ?? '').toLowerCase().includes(term)
        || this.displayRuleCode(p.ruleId).toLowerCase().includes(term));
    });
  }

  onBusinessCodeFilterSearchInput(event: Event): void {
    const target = event?.target as HTMLInputElement | null;
    this.businessCodeFilterSearch = target?.value ?? '';
    this.recomputeFilteredCodes();
  }

  clearBusinessCodeFilterSearch(event?: Event): void {
    event?.stopPropagation();
    this.businessCodeFilterSearch = '';
    this.recomputeFilteredCodes();
    // Clicking the ✕ moves focus to the button; put it back in the box.
    this.focusRuleFilterSearch();
  }

  onBusinessCodeFilterOpenedChange(opened: boolean): void {
    if (opened) {
      this.focusRuleFilterSearch();
    } else if (this.businessCodeFilterSearch) {
      // Reset on close so the next open starts fresh. Selection is preserved
      // separately via selectedRuleIds, so hidden-but-selected codes survive.
      this.businessCodeFilterSearch = '';
      this.recomputeFilteredCodes();
    }
  }

  // mat-select renders its panel lazily in a CDK overlay and keeps keyboard
  // focus on the trigger (aria-activedescendant), so a one-shot focus on open
  // is racy — the input may not be in the DOM yet, or focus lands before the
  // panel settles. Poll the live overlay for the input by its panel class and
  // focus it, retrying briefly until it actually holds focus.
  private focusRuleFilterSearch(attempt = 0): void {
    if (typeof document === 'undefined') return;
    setTimeout(() => {
      const el = document.querySelector(
        '.mat-mdc-select-panel.rule-filter-panel .rule-filter-search__input'
      ) as HTMLInputElement | null;
      if (el && document.activeElement !== el) {
        el.focus();
      }
      if ((!el || document.activeElement !== el) && attempt < 5) {
        this.focusRuleFilterSearch(attempt + 1);
      }
    }, attempt === 0 ? 0 : 40);
  }

  private get businessErrorsAfterCodeFilter(): TransmissionError[] {
    const base = this.businessErrorsAfterTypeFilter;
    const sel = this.selectedRuleIds;
    if (sel.length === 0) return base;
    const NOTES = EliteFormValidationErrorComponent.NOTES_FILTER_ID;
    const notesFilterOn = sel.includes(NOTES);
    const codeSet = new Set(sel.filter(id => id !== NOTES));
    return base.filter(e => {
      if (notesFilterOn) {
        const rid = this.effectiveRuleIdForError(e);
        const hasNote = rid != null && this.rulesById.get(rid)?.note != null;
        if (!hasNote) return false;
      }
      if (codeSet.size === 0) return true;
      const key = this.ruleGroupingKey(e);
      return key !== '__UNKNOWN__' && codeSet.has(key);
    });
  }

  get oecdMetricCounts(): Record<OecdBucket, number> {
    const out: Record<OecdBucket, number> = { file: 0, severe: 0, other: 0 };
    for (const e of this.displayedErrors) {
      const b = this.resolveOecdBucket(this.effectiveRuleIdForError(e) ?? undefined);
      if (b != null) out[b]++;
    }
    return out;
  }

  // Count of currently-visible (post-toggle, post-bucket) errors whose rule
  // has an editorial ValidationNote attached. Drives the "Notes (x)" row in
  // the code-filter dropdown.
  get notesFilterCount(): number {
    let n = 0;
    for (const e of this.businessErrorsAfterTypeFilter) {
      const rid = this.effectiveRuleIdForError(e);
      if (rid != null && this.rulesById.get(rid)?.note != null) n++;
    }
    return n;
  }

  get hasAnyNotedErrors(): boolean {
    for (const e of this.displayedErrors) {
      const rid = this.effectiveRuleIdForError(e);
      if (rid != null && this.rulesById.get(rid)?.note != null) return true;
    }
    return false;
  }

  readonly notesFilterId = EliteFormValidationErrorComponent.NOTES_FILTER_ID;

  oecdTooltipTitle(cat: { tooltipHeading: string; codes: string }): string {
    return `${cat.tooltipHeading} (${cat.codes})`;
  }

  oecdMetricTooltipPlainText(cat: { tooltipHeading: string; codes: string; tooltip: string }): string {
    return `${this.oecdTooltipTitle(cat)}\n\n${cat.tooltip}`;
  }

  toggleOecdBucket(bucket: OecdBucket): void {
    const arr = this.selectedOecdBuckets;
    const i = arr.indexOf(bucket);
    this.selectedOecdBuckets = i >= 0 ? arr.filter((_, idx) => idx !== i) : [...arr, bucket];
    this.expandedBusinessGroupRuleIds.clear();
    this.pruneSelectedRuleIdsAfterDataChange();
    if (this.selectedOecdBuckets.length === 0) {
      this.expandedBucketAccordion = null;
    } else if (this.selectedOecdBuckets.length === 1) {
      this.expandedBucketAccordion = this.selectedOecdBuckets[0];
    } else if (this.expandedBucketAccordion != null
               && !this.selectedOecdBuckets.includes(this.expandedBucketAccordion)) {
      this.expandedBucketAccordion = null;
    }
    this.selectFirstVisibleBusinessRow();
  }

  private selectFirstVisibleBusinessRow(): void {
    let first: TransmissionError | undefined;
    if (this.isBusinessGroupedView) {
      const focusBucket = this.expandedBucketAccordion ?? this.selectedOecdBuckets[0];
      if (focusBucket) {
        first = this.businessGroupedRowsForBucket(focusBucket)[0]?.instances?.[0];
      }
    } else {
      first = this.businessFlatDisplayErrors[0];
    }
    this.selectedErrorSequenceId = first?.errorSequenceId ?? null;
  }

  onBucketAccordionClosed(bucket: OecdBucket): void {
    if (this.expandedBucketAccordion === bucket) {
      this.expandedBucketAccordion = null;
    }
  }

  onBucketAccordionOpened(bucket: OecdBucket): void {
    this.expandedBucketAccordion = bucket;
    const first = this.businessGroupedRowsForBucket(bucket)[0]?.instances?.[0];
    if (first) this.selectedErrorSequenceId = first.errorSequenceId;
  }

  isOecdBucketSelected(bucket: OecdBucket): boolean {
    return this.selectedOecdBuckets.includes(bucket);
  }

  get isBusinessGroupedView(): boolean {
    return this.selectedOecdBuckets.length > 0;
  }

  get businessFlatDisplayErrors(): TransmissionError[] {
    return this.sortBySequenceId(this.businessErrorsAfterCodeFilter);
  }

  get businessGroupedRows(): BusinessErrorGroupRow[] {
    const errors = this.sortBySequenceId(this.businessErrorsAfterCodeFilter);
    const byRule = new Map<string, TransmissionError[]>();
    for (const e of errors) {
      const id = this.ruleGroupingKey(e);
      if (!byRule.has(id)) byRule.set(id, []);
      byRule.get(id).push(e);
    }
    const keys = [...byRule.keys()].sort((a, b) => this.compareRuleIdsNumeric(a, b));
    return keys.map((ruleId, idx) => {
      const instances = byRule.get(ruleId)!;
      const bucket: OecdBucket =
        ruleId === '__UNKNOWN__' ? 'other' : (this.resolveOecdBucket(ruleId) ?? 'other');
      const pub = ruleId === '__UNKNOWN__' ? undefined : this.rulesById.get(ruleId);
      const fromPub = (pub?.validationMessage || pub?.validationRule || '').trim();
      const fromInstance = this.primaryErrorNarrative(instances[0]).trimEnd();
      return {
        ruleId,
        bucket,
        summaryMessage: fromPub || fromInstance,
        instances,
        groupIndex: idx + 1
      };
    });
  }

  businessGroupedRowsForBucket(bucket: OecdBucket): BusinessErrorGroupRow[] {
    return this.businessGroupedRows.filter(g => g.bucket === bucket);
  }

  businessFlatDisplayErrorsForBucket(bucket: OecdBucket): TransmissionError[] {
    return this.businessFlatDisplayErrors.filter(e => {
      const b = this.resolveOecdBucket(this.effectiveRuleIdForError(e) ?? undefined);
      return b === bucket;
    });
  }

  onBusinessCodesFilterChange(ids: string[] | string | null): void {
    const emitted = Array.isArray(ids) ? ids.filter(Boolean) : [];
    const term = (this.businessCodeFilterSearch ?? '').trim();
    if (!term) {
      this.selectedRuleIds = emitted;
      return;
    }
    // While a search term hides part of the catalogue, mat-select reports only
    // the currently-visible selected options. Merge back any previously-selected
    // ids that the filter is hiding so typing + toggling never silently drops a
    // selection. (selectionChange fires before our setter runs, so selectedRuleIds
    // still holds the pre-change selection here.)
    const visible = new Set(this._filteredCodesForDropdown.map(r => r.ruleId));
    if (this._notesRowMatchesSearch && this.hasAnyNotedErrors) {
      visible.add(EliteFormValidationErrorComponent.NOTES_FILTER_ID);
    }
    const preserved = this.selectedRuleIds.filter(id => !visible.has(id));
    this.selectedRuleIds = [...new Set([...emitted, ...preserved])];
  }

  clearBusinessCodeFilters(): void {
    this.selectedRuleIds = [];
  }

  get businessCodesFilterTriggerLabel(): string {
    const n = this.selectedRuleIds.length;
    return n === 0 ? 'Filter by error code' : `${n} selected`;
  }

  badgeClassForBucket(bucket: OecdBucket): string {
    if (bucket === 'file') return 'error-code-badge error-code-badge--file';
    if (bucket === 'severe') return 'error-code-badge error-code-badge--severe';
    return 'error-code-badge error-code-badge--other';
  }

  toggleGroupExpanded(group: BusinessErrorGroupRow, ev?: Event): void {
    ev?.stopPropagation();
    const set = this.expandedBusinessGroupRuleIds;
    const willExpand = !set.has(group.ruleId);
    if (willExpand) {
      set.add(group.ruleId);
      const first = group.instances?.[0];
      if (first) this.selectedErrorSequenceId = first.errorSequenceId;
    } else {
      set.delete(group.ruleId);
    }
  }

  isBusinessGroupExpanded(ruleId: string): boolean {
    return this.expandedBusinessGroupRuleIds.has(ruleId);
  }

  errorCodeBadgeBucket(error: TransmissionError): OecdBucket | null {
    return this.resolveOecdBucket(this.effectiveRuleIdForError(error) ?? undefined);
  }

  displayRuleCode(ruleIdKey: string): string {
    if (ruleIdKey === '__UNKNOWN__') return '—';
    return (ruleIdKey || '').replace(/-/g, ' ');
  }

  // Pre-computed `<rule code> (<country>)` label used by the error-row badges.
  // Country comes from the published-rule catalogue via rulesById; falls back
  // to plain code when the rule isn't in the catalogue. A bundle's stub names
  // its authority instead (FM-P-314): `VR.ATO.CBC.000034 (ATO)`.
  // Memoised on rulesById's reference so we don't allocate fresh strings every
  // Angular change-detection cycle.
  private _badgeLabelCacheKey: Map<string, PublishedValidationRule> | null = null;
  private _badgeLabelCache = new Map<string, string>();

  displayBadgeLabel(ruleId: string | null | undefined): string {
    if (!ruleId) return '';
    if (ruleId === '__UNKNOWN__') return '—';
    if (this.rulesById !== this._badgeLabelCacheKey) {
      this._badgeLabelCacheKey = this.rulesById;
      this._badgeLabelCache.clear();
    }
    const cached = this._badgeLabelCache.get(ruleId);
    if (cached !== undefined) return cached;
    const cleaned = ruleId.replace(/-/g, ' ');
    const rule = this.rulesById.get(ruleId);
    const cc = rule?.countryCode;
    const label = rule?.authority || (cc === 'GB' ? 'UK' : cc);
    const result = label ? `${cleaned} (${label})` : cleaned;
    this._badgeLabelCache.set(ruleId, result);
    return result;
  }

  trackByBusinessGroup(_: number, g: BusinessErrorGroupRow): string {
    return `${g.ruleId}-${g.groupIndex}`;
  }

  get businessTableEmpty(): boolean {
    return this.isBusinessGroupedView
      ? this.businessGroupedRows.length === 0
      : this.businessFlatDisplayErrors.length === 0;
  }

  getTooltipText(error: TransmissionError): string {
    const rid = this.effectiveRuleIdForError(error);
    if (rid) {
      const rule = this.rulesById.get(rid);
      if (rule?.validationRule) return rule.validationRule;
    }
    return error.originalValidationMessage ?? '';
  }

  setActiveTab(tab: 'validation' | 'business' | 'allRules'): void {
    this.unSub$.next();
    this.unSub$.complete();
    this.unSub$ = new Subject<void>();
    this.isLoading = false;
    this.isInitialLoading = false;
    this.isDownloading = false;
    this.tabState.validation.selectedRuleIds = [];
    this.tabState.validation.selectedOecdBuckets = [];
    this.tabState.business.selectedRuleIds = [];
    this.tabState.business.selectedOecdBuckets = [];
    this.expandedBusinessGroupRuleIds.clear();
    this.expandedBucketAccordion = null;
    this.businessCodeFilterSearch = '';
    this.selectedErrorSequenceId = null;
    this.activeTab = tab;
    this.refreshDerivedState();
    if (tab === 'allRules') {
      this.loadAllRulesIfNeeded();
    } else if (!this.tabState[tab].hasGenerated) {
      this.loadValidationErrors(true);
    } else {
      const first = this.sortBySequenceId(this.current.errors)[0];
      this.selectedErrorSequenceId = first?.errorSequenceId ?? null;
    }
  }

  loadAllRulesIfNeeded(): void {
    if (this.hasLoadedAllRulesOnce) return;
    if (!this.responseId) return;
    this.hasLoadedAllRulesOnce = true;
    this.tabState.allRules.isLoading = true;
    const query = new PlatformQuery();
    query.name = 'GetXmlPlaygroundAllRulesQuery';
    query.api = Api.FilingManagerApi;
    query.fieldValues['ResponseId'] = this.responseId;
    this.platformQueryService.executeQuery(query).pipe(
      takeUntil(this.unSub$)
    ).subscribe({
      next: (response) => {
        const raw = response?.fieldValues?.['AllRules'];
        this.allRulesEntries = Array.isArray(raw) ? (raw as AllRulesEntry[]) : [];
        this.tabState.allRules.isLoading = false;
        this.tabState.allRules.isProcessComplete = true;
        this.tabState.allRules.hasGenerated = true;
      },
      error: () => {
        this.allRulesEntries = [];
        this.tabState.allRules.isLoading = false;
        this.tabState.allRules.isProcessComplete = true;
        this.tabState.allRules.hasGenerated = true;
      }
    });
  }

  get filteredAllRulesEntries(): AllRulesEntry[] {
    const term = (this.allRulesSearchTerm ?? '').trim().toLowerCase();
    const buckets = this.allRulesSelectedBuckets;
    return this.allRulesEntries.filter(e => {
      if (buckets.length > 0 && !buckets.includes(e.bucket)) return false;
      if (term.length === 0) return true;
      if ((e.ruleId ?? '').toLowerCase().includes(term)) return true;
      return (e.paths ?? []).some(p =>
        (p.path ?? '').toLowerCase().includes(term)
        || (p.sourceRef ?? '').toLowerCase().includes(term));
    });
  }

  pathsForEntry(entry: AllRulesEntry): AllRulesPath[] {
    const term = (this.allRulesSearchTerm ?? '').trim().toLowerCase();
    const paths = entry.paths ?? [];
    if (term.length === 0) return paths;
    // Rule matched on its id alone — keep every path so the expanded row isn't empty.
    if ((entry.ruleId ?? '').toLowerCase().includes(term)) return paths;
    return paths.filter(p =>
      (p.path ?? '').toLowerCase().includes(term)
      || (p.sourceRef ?? '').toLowerCase().includes(term));
  }

  toggleAllRulesExpansion(ruleId: string): void {
    if (this.allRulesExpanded.has(ruleId)) {
      this.allRulesExpanded.delete(ruleId);
    } else {
      this.allRulesExpanded.add(ruleId);
    }
  }

  allRulesTooltip(entry: AllRulesEntry): string {
    const rule = (entry?.validationRule ?? '').trim();
    if (rule) return rule;
    const msg = (entry?.validationMessage ?? '').trim();
    if (msg) return msg;
    const t = (entry?.errorType ?? '').trim();
    return t ? `${entry.ruleId} — ${t}` : (entry?.ruleId ?? '');
  }

  isAllRulesExpanded(ruleId: string): boolean {
    return this.allRulesExpanded.has(ruleId);
  }

  onAllRulesPanelOpened(ruleId: string): void {
    this.allRulesExpanded.add(ruleId);
  }

  onAllRulesPanelClosed(ruleId: string): void {
    this.allRulesExpanded.delete(ruleId);
  }

  readonly allRulesBuckets: ReadonlyArray<OecdBucket> = ['file', 'severe', 'other'];

  private static readonly ALL_RULES_BUCKET_LABELS: Record<OecdBucket, string> = {
    file: 'File',
    severe: 'Severe',
    other: 'Other'
  };

  allRulesBucketLabel(bucket: OecdBucket): string {
    return EliteFormValidationErrorComponent.ALL_RULES_BUCKET_LABELS[bucket];
  }

  toggleAllRulesBucket(bucket: OecdBucket): void {
    const idx = this.allRulesSelectedBuckets.indexOf(bucket);
    if (idx >= 0) {
      this.allRulesSelectedBuckets = this.allRulesSelectedBuckets.filter(b => b !== bucket);
    } else {
      this.allRulesSelectedBuckets = [...this.allRulesSelectedBuckets, bucket];
    }
  }

  private static readonly SECTION_NUMBER_RE = /^\d+(?:\.[A-Za-z0-9]+)*$/;

  onAllRulesPathClick(path: AllRulesPath, event: MouseEvent): void {
    event?.stopPropagation();

    // Primary path: the backend parsed source_ref into a sheet + cell/row.
    if (path?.isNavigable) {
      this.scrollToPlaygroundSourceCell(
        path.sheetName ?? '',
        path.cellAddress ?? '',
        path.row ?? -1);
      return;
    }

    const ref = EliteFormValidationErrorComponent.normalizeSectionRef(path?.sourceRef ?? '');
    if (ref && EliteFormValidationErrorComponent.SECTION_NUMBER_RE.test(ref)) {
      const sheet = this.findSheetByLongestSectionPrefix(ref);
      if (sheet) {
        this.scrollToPlaygroundSourceCell(sheet, '', -1);
        return;
      }
    }

    this.snackBarService.showWarning(
      `No navigation target for "${path?.sourceRef ?? ''}".`);
  }

  private static readonly PAREN_LIST_RE = /\(([^)]+)\)/g;
  private static readonly TRAILING_RANGE_RE = /-[A-Za-z0-9]+$/;
  private static readonly LEADING_SECTION_RE = /^(\d+(?:\.[A-Za-z0-9]+)*)/;

  private static normalizeSectionRef(raw: string): string {
    if (!raw) return '';
    let s = raw.trim();
    s = s.replace(EliteFormValidationErrorComponent.PAREN_LIST_RE,
      (_match, inner) => (inner as string).split(',')[0].trim());
    s = s.replace(EliteFormValidationErrorComponent.TRAILING_RANGE_RE, '');
    const m = EliteFormValidationErrorComponent.LEADING_SECTION_RE.exec(s);
    return m ? m[1] : s;
  }

  private findSheetByLongestSectionPrefix(sectionRef: string): string | null {
    const worksheets = this.flexSheetService.getWorksheets() || [];
    if (worksheets.length === 0) return null;
    const sheetNames = new Set<string>();
    for (const ws of worksheets) {
      if (ws?.name) sheetNames.add(ws.name);
      const mapping = (ws as any)?.mappingWorksheetName;
      if (mapping) sheetNames.add(mapping);
    }
    const segments = sectionRef.split('.');
    for (let n = segments.length; n >= 1; n--) {
      const candidate = `Sch. ${segments.slice(0, n).join('.')}`;
      if (sheetNames.has(candidate)) return candidate;
    }
    return null;
  }

  trackByRuleId(_index: number, entry: AllRulesEntry): string {
    return entry.ruleId;
  }

  get visibleTabs(): Array<'validation' | 'business' | 'allRules'> {
    const tabs: Array<'validation' | 'business' | 'allRules'> = [];
    if (this.homeSideBarItem?.fieldValues?.['IsValidationRuleAvailable'] !== false) tabs.push('validation');
    if (this.homeSideBarItem?.fieldValues?.['IsBusinessRuleAvailable'] === true) tabs.push('business');
    if (this.isAllRulesAvailable) tabs.push('allRules');
    return tabs;
  }

  get selectedTabIndex(): number {
    const idx = this.visibleTabs.indexOf(this.activeTab);
    return idx >= 0 ? idx : 0;
  }

  onTabIndexChange(idx: number): void {
    const next = this.visibleTabs[idx];
    if (next && next !== this.activeTab) {
      this.setActiveTab(next);
    }
  }

  get isAllRulesLoading(): boolean {
    return this.tabState.allRules.isLoading;
  }

  onAllRulesSearchInput(event: Event): void {
    const target = event?.target as HTMLInputElement | null;
    this.allRulesSearchTerm = target?.value ?? '';
  }

  scrollToPlaygroundSourceCell(sheetName: string, cellAddress: string, row: number): void {
    if (!sheetName) {
      this.snackBarService.showWarning('This rule has no navigable source location.');
      return;
    }
    const worksheets = this.flexSheetService.getWorksheets() || [];
    // eslint-disable-next-line no-console
    console.log('[AllRules nav] sheetName=', sheetName, 'cellAddress=', cellAddress, 'row=', row,
      'loaded worksheets=', worksheets.map(w => w?.name));

    if (worksheets.length === 0) {
      this.snackBarService.showWarning(
        'Open the playground workbook first (via "Open Excel" on the grid). The All Rules tab navigates the currently loaded workbook.');
      return;
    }

    const sheet = worksheets.find(ws =>
      ws?.name === sheetName || (ws as any)?.mappingWorksheetName === sheetName);
    if (!sheet) {
      const loaded = worksheets.map(w => w?.name).filter(Boolean).join(', ');
      this.snackBarService.showWarning(
        `Sheet "${sheetName}" is not in the loaded workbook (loaded: ${loaded}).`);
      return;
    }

    if (cellAddress) {
      // row/col = -1 → backend parses WorksheetCellAddress.
      this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(
        sheet.id, -1, -1, cellAddress);
      return;
    }

    if (typeof row === 'number' && row >= 0) {
      // OECD "Input Entity Row 141" is 1-based; backend expects 0-based row.
      this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(
        sheet.id, row - 1, 0, null);
      return;
    }
    this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(
      sheet.id, 0, 0, null);
  }

  get isReadyToValidate(): boolean {
    return !this.hasGenerated && !this.isLoading && !this.isInitialLoading;
  }

  get hasValidationErrors(): boolean {
    return this.hasGenerated
      && !this.hasMappingException
      && this.isProcessComplete
      && this.validationErrors.length > 0;
  }

  get hasMappingIssue(): boolean {
    return this.hasGenerated
      && !this.isLoading
      && this.hasMappingException
      && !this.isProcessComplete;
  }

  get isAllChecksPassed(): boolean {
    return this.hasGenerated
      && !this.isLoading
      && this.isProcessComplete
      && !this.hasMappingException
      && this.validationErrors.length === 0;
  }

  get hasMappingException(): boolean {
    return this.validationErrors?.some(e => e.metaData?.HasExcelMappingException === true) ?? false;
  }

  get mappingExceptionError(): TransmissionError | null {
    return this.validationErrors?.find(e => e.metaData?.HasExcelMappingException === true) ?? null;
  }

  get mappingTicketDisplayId(): string {
    return this.mappingExceptionError?.metaData?.TicketDisplayId ?? '';
  }

  get mappingTicketUrl(): string {
    const ticketId = this.mappingExceptionError?.metaData?.TicketId;
    if (!ticketId) return '';
    const baseUrl = Configuration.getSupportUrl() || '';
    if (!baseUrl) return '';
    return `${baseUrl.replace(/\/$/, '')}/${ticketId}`;
  }

  get isQatarCluster(): boolean {
    return (Configuration.getConfig('Cluster') ?? '').toString().toLowerCase() === 'qatar';
  }

  trackByError(_: number, error: TransmissionError): number {
    return error.errorSequenceId;
  }

  selectRow(error: TransmissionError): void {
    const id = error?.errorSequenceId ?? null;
    if (id == null) return;
    this.selectedErrorSequenceId = this.selectedErrorSequenceId === id ? null : id;
  }

  isRowSelected(error: TransmissionError): boolean {
    return error != null
      && this.selectedErrorSequenceId != null
      && error.errorSequenceId === this.selectedErrorSequenceId;
  }

  isGoToFieldAvailableInHost(error: TransmissionError): boolean {
    const md = error?.metaData;
    if (!md) return false;
    // Form-type-specific precise paths take priority.
    switch (this.currentFormType) {
      case 'elite':
        if (md.fieldKey && md.dataItemId) return true;
        return false;
      case 'xml':
        if (md.XmlPath) return true;
        return false;
      case 'excel':
        if (md.WorkbookId && md.WorksheetId) return true;
        if (md.SourceRefIsNavigable && md.SourceRefSheetName) return true;
        const sourceRef = (md.SourceRef ?? '').toString().trim();
        const sheetName = this.findSheetByLongestSectionPrefix(sourceRef);
        return !!sourceRef && !!sheetName;
    }
  }

  onClickGoToFieldButton(error: TransmissionError): void {
    this.rowClick.emit(error);
    const fieldKey = error.metaData?.fieldKey ?? '';
    const dataItemId = error.metaData?.dataItemId ?? '';
    const workbookId = error.metaData?.WorkbookId ?? '';
    const worksheetId = error.metaData?.WorksheetId ?? '';
    const xmlPath = error.metaData?.XmlPath ?? '';
    const hasMountedWorkbook = this.currentFormType === 'excel';
    const xmlViewerInDom = (this.isXmlPlaygroundEntry && typeof document !== 'undefined'
      && document.querySelector('app-xml-viewer-tabs') !== null)
      || this.currentFormType === 'xml';
    const sourceRef = (error.metaData?.SourceRef ?? '').toString().trim();

    if (fieldKey && dataItemId) {
      const pageIndex = typeof error.metaData?.pageIndex === 'number' && error.metaData.pageIndex >= 0
          ? error.metaData.pageIndex : 0;
      this.scrollToEliteFormField(fieldKey, pageIndex, dataItemId);
    } else if (xmlPath && xmlViewerInDom) {
      this.xmlHighlightService.highlight({ path: xmlPath, field: error.metaData?.XmlField });
    } else if (workbookId && worksheetId && hasMountedWorkbook) {
      const row = error.metaData?.Row ?? -1;
      const column = error.metaData?.Column ?? -1;
      const gridRow = error.metaData?.GridRow ?? -1;
      const gridColumn = error.metaData?.GridColumn ?? -1;
      const rowEnd = error.metaData?.RowEnd ?? -1;
      const columnEnd = error.metaData?.ColumnEnd ?? -1;
      this.scrollToExcelFormSection(worksheetId, gridRow, gridColumn, row, column, rowEnd, columnEnd);
    } else if (hasMountedWorkbook && error.metaData?.SourceRefIsNavigable
               && error.metaData?.SourceRefSheetName) {
      // Cell/Row precise navigation (mirrors All Rules tier-1 nav, driven by
      // SourceRefParser.Parse output stamped on the error by the backend).
      const worksheets = this.flexSheetService.getWorksheets() || [];
      const targetSheet = error.metaData.SourceRefSheetName;
      const ws = worksheets.find(w =>
        w?.name === targetSheet || (w as any)?.mappingWorksheetName === targetSheet);
      if (ws) {
        const cellAddress = (error.metaData.SourceRefCellAddress ?? '').toString();
        const oneBasedRow = typeof error.metaData.SourceRefRow === 'number'
          ? error.metaData.SourceRefRow : -1;
        if (cellAddress) {
          // Cell pattern: backend resolves WorksheetCellAddress (row/col = -1).
          this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(
            ws.id, -1, -1, cellAddress);
        } else if (oneBasedRow >= 0) {
          // Row pattern: OECD source_refs are 1-based; backend wants 0-based.
          this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(
            ws.id, oneBasedRow - 1, 0, '');
        } else {
          this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(
            ws.id, 0, 0, '');
        }
      }
    } else if (hasMountedWorkbook && sourceRef) {
      const sheetName = this.findSheetByLongestSectionPrefix(sourceRef);
      if (sheetName) {
        const worksheets = this.flexSheetService.getWorksheets() || [];
        const ws = worksheets.find(w =>
          w?.name === sheetName || (w as any)?.mappingWorksheetName === sheetName);
        if (ws) {
          this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(ws.id, 0, 0, '');
        }
      }
    } else{
      return;
    }
  }

  openErrorInXatbot(error: TransmissionError, event: MouseEvent): void {
    event.stopPropagation();
    // Keep the prompt minimal: the form name, the error type, and the
    // errorSequenceId. The backend (XmlAnalyzer) resolves the rest from the
    // saved validation results — no error detail needs to travel in the prompt.
    const errorType = this.activeTab === 'business' ? 'business' : 'validation';
    const ruleLabel = this.displayBadgeLabel(this.effectiveRuleIdForError(error));
    const rulePart = ruleLabel ? ` "${ruleLabel}"` : '';
    const formLabel = [this.formAutoId, this.formName].filter(Boolean).join('-');
    const formRef = formLabel ? ` in form "${formLabel}"` : '';
    const question =
      `Step-by-step guidance to resolve ${errorType} error #${error.errorSequenceId}${rulePart}${formRef}.`;
    this.xatBotDataService.openFloatingXatBotWithQuestion(question);
  }

  scrollToEliteFormField(fieldKey: string, pageIndex: number, dataItemId: string): void {
    const selectedFieldKey = fieldKey + '--' + dataItemId;
    this.tocService.selectField(selectedFieldKey);
    this.tocService.goToPage(pageIndex);
    this.tocService.scrollTo(selectedFieldKey);
  }

  scrollToExcelFormSection(worksheetId: string, gridRow: number, gridColumn: number, row: number, column: number, rowEnd: number, columnEnd: number): void {
    if (!worksheetId) return;
    const worksheetIndex = this.flexSheetService.getWorksheets().findIndex(ws => ws.id === worksheetId);
    if (worksheetIndex < 0) return;
    if (gridRow == -1) gridRow = 0;
    if (gridColumn == -1) gridColumn = 0;
    if (rowEnd == -1) rowEnd = row;
    if (columnEnd == -1) columnEnd = column;

    let cellAddress, callback;
    this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(worksheetId, row, column, cellAddress, callback, rowEnd, columnEnd);
  }

  ngOnDestroy(): void {
    this.unSub$.next();
    this.unSub$.complete();
  }

  resolveCalculationError(err) {
    const command = new PlatformCommand('CalculationErrorResolverCommand', Api.FilingManagerApi);
    command.fieldValues['CalculationErrorFields'] = [
      {'Title': 'TopUpTaxPercentage','Key': 'TopUpTaxPercentage','XPath': '','Value': 0.15,'MetaDataKey': '','DataType': 'decimal'},
      {'Title': 'ExcessProfits','Key': 'ExcessProfits','XPath': '','Value': 6269575,'MetaDataKey': '','DataType': 'decimal'},
      {'Title': 'NONArt4.1.5','Key': 'NONArt4.1.5','XPath': '','Value': 0,'MetaDataKey': '','DataType': 'decimal'},
      {'Title': 'AdditionalTopUpTax','Key': 'AdditionalTopUpTax','XPath': '','Value': 1,'MetaDataKey': '','DataType': 'decimal'},
      {'Title': 'Art4.1.5','Key': 'Art4.1.5','XPath': '','Value': 0,'MetaDataKey': '','DataType': 'decimal'},
      {'Title': 'QDMTT','Key': 'QDMTT','XPath': '','Value': 940436 ,'MetaDataKey': '','DataType': 'decimal'},
      {'Title ': 'Amount','Key': 'Amount','XPath': '','Value': 1,'MetaDataKey': '','DataType': 'decimal'}
    ];
    command.fieldValues['Formula']='(TopUpTaxPercentage × ExcessProfits) + (NONArt4.1.5/AdditionalTopUpTax + Art4.1.5/AdditionalTopUpTax) - QDMTT/Amount';
    command.fieldValues['ExpectedValue']= 12;
    this.commandService.executePlatformCommand(command).subscribe(response => {
      console.log(response);
    })
  }
}
